import { graphql, Link } from 'gatsby';
import { GatsbyImage, getSrc, getImage } from 'gatsby-plugin-image';
import React from 'react';
import Layout from '../components/layout';
import Disclaimer from '../components/disclaimer';
import RenderSlices from '../components/render-slices';
import ShareSocialIcons from '../components/share-social-icons';
import Seo from '../components/seo';
import BreadcrumbList from '../components/breadcrumb-list';
import PubliftPageInit from '../components/ads/publift-page-init';
import PubliftAd from '../components/ads/publift-ad';
import AdColumn from '../components/ads/ad-column';
import ArticleCard from '../components/article-card';
import RenderCta from '../components/cta/render-cta';

import {
    articleHeader,
    articleHeaderRow,
    articleHeaderText,
    categoryDesc,
    articleImageWrapper,
    articleImage,
    authorImage,
    articleAuthor,
} from './article.module.scss';

const Article = ({ data: { article, moreArticles }, location }) => {
    const articleData = article.data;

    const {
        body,
        category,
        cover_image,
        cover_image_sizing,
        cover_image_background,
        date,
        formattedDate,
        title,
    } = articleData;

    const categoryName = category.document.data.name;
    const description = articleData.description || category.document.data.description;

    const articleCoverImgUrl =
        cover_image && cover_image.gatsbyImageData
            ? cover_image.gatsbyImageData
            : category.document.data.image;
    const articleCoverImgAlt = cover_image && cover_image.alt ? cover_image.alt : title;

    const publisher = articleData.publisher.document ? articleData.publisher.document.data : null;
    const author = articleData.author.document ? articleData.author.document.data : null;
    const authorName = publisher ? publisher.name : author.name;
    let authorImageUrl = null;

    if (publisher && publisher.image) {
        authorImageUrl = publisher.image;
    } else if (author && author.image) {
        authorImageUrl = author.image;
    }

    const breadcrumbs = [
        {
            name: 'Insights',
            url: '/insights',
        },
        {
            name: title,
            url: `/insights/${article.uid}`,
        },
    ];

    return (
        <Layout>
            <Seo
                title={title}
                pathname={location.pathname}
                description={description}
                date={article.last_publication_date}
                article={{
                    title: title,
                    description: description,
                    image: getSrc(articleCoverImgUrl),
                    date: date,
                    author: authorName,
                    category: categoryName,
                }}
            />

            <BreadcrumbList breadcrumbs={breadcrumbs} isHidden={true} />
            <article>
                <header>
                    <div
                        className={`section ${articleHeader}`}
                        style={{ backgroundColor: cover_image_background }}>
                        <div className={`section-inner ${articleHeaderRow}`}>
                            <div className={articleHeaderText}>
                                <h1 className="hero-heading-xl">{title}</h1>
                                <p className={`${categoryDesc}`}>{description}</p>
                                <div className={articleAuthor}>
                                    {authorImageUrl && getImage(authorImageUrl) && (
                                        <GatsbyImage
                                            image={getImage(authorImageUrl)}
                                            className={`${authorImage}`}
                                            alt={`PaycheckCity article by ${authorName}`}
                                            title={`${authorName}`}
                                        />
                                    )}
                                    <span>{authorName}</span>
                                    <span className="text-divider" />
                                    <span>
                                        {formattedDate}
                                        {/* in{ ' ' }
                                        <Link to={`/payroll-tax-insights/${category.uid}`} className={`btn-text-gray ${styles.articleCategory}`}>
                                            {categoryName}
                                        </Link> */}
                                    </span>
                                </div>
                                <div>
                                    <ShareSocialIcons title={title} />
                                </div>
                            </div>
                            {articleCoverImgUrl && getImage(articleCoverImgUrl) && (
                                <div className={articleImageWrapper}>
                                    <GatsbyImage
                                        image={getImage(articleCoverImgUrl)}
                                        className={articleImage}
                                        objectFit={cover_image_sizing}
                                        imgSyle={{ backgroundColor: cover_image_background }}
                                        alt={articleCoverImgAlt}
                                        title={articleCoverImgAlt}
                                        placeholder="blurred"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                <section className="section section-bottom-none">
                    <div className="section-inner flex-row">
                        <div className={`column-main underline-all-links`}>
                            <div className="margin-bottom-sm">
                                {/* GAM 71161633/PCHKCT_paycheckcity/insightarticle_header */}
                                <PubliftAd
                                    adId="23118772627"
                                    mobileHeight="100"
                                    desktopHeight="90"
                                />
                            </div>

                            <RenderSlices allSlices={body} />
                            <div className="margin-top-md">
                                <Disclaimer />
                            </div>
                        </div>
                        {/* GAM 71161633/PCHKCT_paycheckcity/insightarticle_vrec */}
                        <AdColumn publiftAdId="23119311905" side="right" />
                    </div>
                </section>
            </article>

            <section className="section">
                <div className="section-inner">
                    <div className="section-heading-border-wrapper">
                        <h2 className="section-heading">
                            <strong className="text-bold">Keep reading:</strong> more insights
                        </h2>
                    </div>
                    <div className="card-list card-list-wrap-on-sm">
                        {moreArticles.edges.map(({ node }) => (
                            <ArticleCard article={node} key={node.uid} />
                        ))}
                    </div>
                    <div className="text-center margin-bottom-sm">
                        <Link to="/insights" className="btn btn-outline-default">
                            See all insights
                        </Link>
                    </div>
                    {/* GAM 71161633/PCHKCT_paycheckcity/insightarticle_footer */}
                    <PubliftAd adId="23118772633" mobileHeight="280" desktopHeight="250" />
                </div>
            </section>

            <RenderCta type="business-0" name="insights" />
            <PubliftPageInit />
        </Layout>
    );
};

export const query = graphql`query articleQuery($uid: String) {
  article: prismicPccArticle(uid: {eq: $uid}) {
    ...ArticlePageFragment
  }
  moreArticles: allPrismicPccArticle(
    limit: 3
    sort: {data: {date: DESC}}
    filter: {data: {is_hidden: {eq: "No"}}, uid: {ne: $uid}}
  ) {
    edges {
      node {
        ...ArticleCardFragment
      }
    }
  }
}`;

export default Article;
